import { renderScriptPdf, renderScriptDocx, saveBlob, scriptFileName } from '@components/_modals/Content/GenerateVideoScript/helpers/exportScript';
import { studioApi } from './studioApi';
import { uploadAsset } from './uploads';

export const RENDERER_VERSION = 'studio-web-v1';
const MIME_TYPES = { pdf: 'application/pdf', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' };

// Adapt a Studio script document to the existing lesson exporter explicitly.
// Spoken text and directions remain distinct; no pending draft is exported.
export function reelToExport(script, t) {
  return { title: script.title, overview: t('script'), blocks: script.blocks.map(block => ({
    title: t(block.kind), minutes: 0,
    script: `${block.spoken_text}${block.recording_notes ? `\n\n${t('notes')}: ${block.recording_notes}` : ''}`,
  })) };
}

export function renderReel(script, format, t, revisionNumber) {
  const doc = reelToExport(script, t);
  const options = { subtitle: `Reels · ${t('revision', { number: revisionNumber })}`, footer: 'Ginastee Studio',
    labels: { minutes: () => t('spoken'), page: (number, total) => `${number} / ${total}` } };
  const blob = format === 'pdf' ? renderScriptPdf(doc, options) : renderScriptDocx(doc, options);
  return { blob, fileName: `${scriptFileName(doc.title)}.${format}` };
}

// Export V1 (`studio-web-v1`): the server freezes the revision document, the browser
// renders that document, uploads it to the private bucket and completes the export.
// The creator saves the same rendering right away; if persisting it fails the export
// is marked failed and the error says the file was downloaded but not stored.
export async function exportReel({ projectId, revision, format, t, persist = true }) {
  if (!persist) {
    const { blob, fileName } = renderReel(revision.script, format, t, revision.revision_number);
    saveBlob(blob, fileName);
    return null;
  }
  const record = await studioApi.createExport(projectId, { revision_id: revision.id, format });
  let rendered;
  try { rendered = renderReel(record.document, format, t, revision.revision_number); }
  catch (failure) {
    await studioApi.updateExport(record.id, { intent: 'fail', failure_code: 'render_failed' }).catch(() => null);
    throw failure;
  }
  saveBlob(rendered.blob, rendered.fileName);
  try {
    const asset = await uploadAsset({ purpose: 'export', project_id: projectId, export_id: record.id, mime_type: MIME_TYPES[format] }, rendered.blob);
    return await studioApi.updateExport(record.id, { intent: 'complete', asset_id: asset.id, source_digest: record.source_digest, renderer_version: RENDERER_VERSION });
  } catch {
    await studioApi.updateExport(record.id, { intent: 'fail', failure_code: 'upload_failed' }).catch(() => null);
    throw Object.assign(new Error('Studio export not stored'), { studio: true, code: 'export_persist', details: {} });
  }
}
